// © Microsoft Corporation. All rights reserved.

import { BaseExperienceConfig, ChildExperienceValidator, ValidatorSectionName } from "@msnews/core";
import { AppValidator } from "./AppValidator";
import { cloneDeep } from "lodash-es";

/**
 * Config Processor class to filter config
 * @class
 */
export class ConfigProcessor {
    /**
     * experience validator map to be provided by app type. Each entry has a validator name and a validator function associated 
     * with that.
     */
    static expValidatorMap: Map<string, () => boolean> = new Map();

    /**
     * config map that keeps track of original configs which are modified by ConfigProcessor.
     * This is required for partial page refresh scenarios where original config is required for refernce like ReAdding previously removed SD cards
     */
    static expConfigMap: Map<string, BaseExperienceConfig<any>> = new Map();

    /**
     * Registers apptype validators
     * @method
     * @param {AppValidator} apptypeValidator Validator for a specific app type
     * @returns {void}
     */
    public static registerAppValidator(apptypeValidator: AppValidator): void {
        this.expValidatorMap = apptypeValidator.getAllValidators();
    }

    /**
     * process config based on registered validators for current app-type and removes child experience from 
     * section defined in CMS condig
     * @method
     * @param {BaseExperienceConfig<any>} expConfig Experience config object
     * @returns {BaseExperienceConfig<any>} returns filtered experience config
     */
    public static processConfig(expConfig: BaseExperienceConfig<any>, namespace: string): BaseExperienceConfig<any> {
        if (!this.expValidatorMap || this.expValidatorMap.size < 1 || !expConfig || !expConfig.childExpValidatorMap) {
            return expConfig;
        }

        const expMap: { [instanceId: string]: ChildExperienceValidator } = expConfig.childExpValidatorMap;
        let originalConfig: BaseExperienceConfig<any> = null;
        let tempConfig: BaseExperienceConfig<any> = null;

        for (const validatorName in expMap) {
            var childExpValidator = expMap[validatorName];
            if (this.expValidatorMap.has(validatorName)) {
                const validator = this.expValidatorMap.get(validatorName);
                const removeChildExp = validator();

                if (removeChildExp) {
                    if (tempConfig === null) {
                        // always use saved config if exists
                        tempConfig = this.expConfigMap.get(namespace) || expConfig;
                    }

                    // keep a copy of original config
                    if (originalConfig === null) {
                        originalConfig = cloneDeep(tempConfig);
                    }

                    expConfig = this.filterConfig(childExpValidator, tempConfig);
                }
            }
        }

        if (originalConfig) {
            this.expConfigMap.set(namespace, originalConfig);
        }
        // if tempConfig is null, none of the validators returned true.
        // restore expConfig with originalConfig for this case
        else if (tempConfig === null && this.expConfigMap.has(namespace)) {
            expConfig = this.expConfigMap.get(namespace);
        }

        return expConfig;
    }

    /**
     * remove child experience based on the response from registerd validator and config.
     * @method
     * @param {ChildExperienceValidator} childExpValidator Experience config object
     * @param {BaseExperienceConfig<any>} expConfig Experience config object
     * @returns {BaseExperienceConfig<any>} returns filtered experience config
     */
    private static removeFromRiverGridChildren(childExpValidator: ChildExperienceValidator, expConfig: any): BaseExperienceConfig<any> {
        // TODO: River config is in experiences and we should not take dependency of experiences in this project.
        // We should move RiverConfig to core config project or expConfig has to be of type any
        let riverGridChildren = expConfig.riverGridChildren;
        if (riverGridChildren) {
            riverGridChildren.forEach(element => {
                let childElems = element.children;
                if (childElems) {
                    element.children = childElems.filter((childExp) => {
                        const configInfo = childExp.childExperienceConfigInfo;
                        return !configInfo || !childExpValidator.childExperienceConfigInfo
                        || childExpValidator.childExperienceConfigInfo.instanceId !== configInfo.instanceId
                        || childExpValidator.childExperienceConfigInfo.experienceType !== configInfo.experienceType;
                    });
                }
            });
        }

        return expConfig;
    }

    /**
     * Verify if the section provided in CMS schema is supported for removal of child experiences.
     * Currently only riverGridChildren is supported. Support will be extended to other sections.
     * @method
     * @param {ChildExperienceValidator} childExpValidator Experience config object
     * @param {BaseExperienceConfig<any>} expConfig Experience config object
     * @returns {BaseExperienceConfig<any>} returns filtered experience config
     */
    private static filterConfig(childExpValidator: ChildExperienceValidator, expConfig: BaseExperienceConfig<any>): BaseExperienceConfig<any> {
        if (childExpValidator && typeof childExpValidator.sectionName === "string" && childExpValidator.sectionName.toLowerCase() === ValidatorSectionName.riverGridChildren.toLowerCase()) {
            return this.removeFromRiverGridChildren(childExpValidator, expConfig);
        }

        return expConfig;
    }
}